import React, { useEffect, useCallback, useState } from 'react';
import { RefreshCw, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { WorkflowDefinition } from '../../types/workflows';
import { apiClient } from '../../lib/api';

interface WorkflowRun {
  id: string;
  workflow_id: string;
  status: string;
  started_at: string;
  completed_at?: string;
  error_message?: string;
  result?: any;
}

interface WorkflowRunsViewerProps {
  workflow: WorkflowDefinition;
}

export const WorkflowRunsViewer: React.FC<WorkflowRunsViewerProps> = ({
  workflow
}) => {
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState<any>(null);


  const loadRunsAndStats = useCallback(async () => {
    if (!workflow.id) return;

    try {
      setIsLoading(true);
      setError(null);

      const [runsResponse, statsResponse] = await Promise.all([
        apiClient.getWorkflowRuns(workflow.id),
        apiClient.getWorkflowStats(workflow.id)
      ]);

      setRuns(runsResponse.runs || []);
      setStats(statsResponse);
    } catch (err: any) {
      console.error('Error loading workflow runs:', err);
      setError('Failed to load workflow runs');
    } finally {
      setIsLoading(false);
    }
  }, [workflow.id]);

  useEffect(() => {
    if (workflow.id) {
      loadRunsAndStats();
    }
  }, [loadRunsAndStats, workflow.id]);

  const STATUS_CONFIG = {
    completed: {
      icon: <CheckCircle className="w-4 h-4 text-green-500" />,
      color: 'success' as const
    },
    running: {
      icon: <Clock className="w-4 h-4 text-blue-500 animate-spin" />,
      color: 'primary' as const
    },
    failed: {
      icon: <XCircle className="w-4 h-4 text-red-500" />,
      color: 'error' as const
    },
    default: {
      icon: <AlertTriangle className="w-4 h-4 text-yellow-500" />,
      color: 'warning' as const
    }
  };

  const getStatusIcon = (status: string) => {
    const statusKey = status.toLowerCase() as keyof typeof STATUS_CONFIG;
    return STATUS_CONFIG[statusKey]?.icon || STATUS_CONFIG.default.icon;
  };

  const getStatusColor = (status: string) => {
    const statusKey = status.toLowerCase() as keyof typeof STATUS_CONFIG;
    return STATUS_CONFIG[statusKey]?.color || STATUS_CONFIG.default.color;
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const duration = end.getTime() - start.getTime();
    
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Top Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-gray-600">
          View all runs and execution results for this workflow
        </div>
        <Button
          size="sm"
          onClick={loadRunsAndStats}
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-blue-600">{stats.total_runs || 0}</div>
            <div className="text-xs text-blue-800">Total Runs</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-green-600">{stats.successful_runs || 0}</div>
            <div className="text-xs text-green-800">Successful</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-red-600">{stats.failed_runs || 0}</div>
            <div className="text-xs text-red-800">Failed</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-yellow-600">{stats.avg_duration || '0s'}</div>
            <div className="text-xs text-yellow-800">Avg Duration</div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8">
            <div className="text-gray-500">Loading workflow runs...</div>
          </div>
        ) : runs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No runs yet</h3>
            <p className="text-gray-600 mb-6">
              This workflow hasn't been executed yet. Publish and run it to see execution history here.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {runs.map((run) => (
              <div
                key={run.id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getStatusIcon(run.status)}
                      <Badge variant={getStatusColor(run.status)}>
                        {run.status}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        Started {new Date(run.started_at).toLocaleString()}
                      </span>
                    </div>

                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>
                        Duration: {formatDuration(run.started_at, run.completed_at)}
                      </span>
                      {run.completed_at && (
                        <span>
                          Completed: {new Date(run.completed_at).toLocaleString()}
                        </span>
                      )}
                    </div>

                    {run.error_message && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        Error: {run.error_message}
                      </div>
                    )}

                    {run.result && (
                      <div className="mt-2">
                        <button
                          className="text-sm text-blue-600 hover:text-blue-800"
                          onClick={() => {
                            setSelectedResult(run.result);
                            setShowResultModal(true);
                          }}
                        >
                          View Result →
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Result Modal */}
      <Modal
        isOpen={showResultModal}
        onClose={() => {
          setShowResultModal(false);
          setSelectedResult(null);
        }}
        title="Workflow Result"
        size="lg"
      >
        <div className="p-4">
          <pre className="bg-neutral-50 border border-neutral-200 rounded p-4 text-xs overflow-auto max-h-96">
            {JSON.stringify(selectedResult, null, 2)}
          </pre>
        </div>
      </Modal>
    </div>
  );
};